import React from 'react';
import { Navigate } from 'react-router-dom';

// Define the correct user roles that match your backend UserRole enum
type UserRole = 'USER' | 'ADMIN';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  company?: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles = [] 
}) => {
  // Get user from localStorage with proper error handling
  let user: User | null = null;
  
  try {
    const userString = localStorage.getItem('user');
    if (userString) {
      user = JSON.parse(userString) as User;
    }
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error);
    // Clear corrupted data
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  }
  
  // If no user is logged in, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // If user account is not active, clear session and redirect to login
  if (!user.isActive) {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    return <Navigate to="/login" replace />;
  }
  
  // If specific roles are required and user doesn't have permission
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate page based on user's actual role
    const redirectPath = user.role === 'ADMIN' ? '/admin' : '/feed';
    return <Navigate to={redirectPath} replace />;
  }
  
  // User is authenticated and authorized
  return <>{children}</>;
};

export default ProtectedRoute;